import { gql, useMutation, useQuery } from "@apollo/client";
import { useCallback } from "react";

const GET_SETTINGS = gql`
  query GetSettings {
    me {
      id
      email
      displayName
      firstName
      lastName
      jobTitle
      notificationChannel
      notificationFrequency
    }
  }
`;

const UPDATE_PROFILE = gql`
  mutation UpdateProfileSettings($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      displayName
      firstName
      lastName
      jobTitle
      notificationChannel
      notificationFrequency
    }
  }
`;

const DELETE_ACCOUNT = gql`
  mutation DeleteAccount {
    deleteAccount
  }
`;

export interface AccountSettings {
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  notificationChannel: string | null;
  notificationFrequency: string | null;
}

export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  jobTitle?: string;
  notificationChannel?: string;
  notificationFrequency?: string;
}

export function useSettings() {
  const { data, loading: fetching } = useQuery<{ me: AccountSettings | null }>(GET_SETTINGS, {
    fetchPolicy: "cache-and-network",
  });

  const [updateMutation, { loading: saving }] = useMutation(UPDATE_PROFILE);
  const [deleteMutation, { loading: deleting }] = useMutation(DELETE_ACCOUNT);

  const updateProfile = useCallback(
    async (input: UpdateProfileInput) => {
      const { data } = await updateMutation({ variables: { input } });
      return data?.updateProfile as AccountSettings | undefined;
    },
    [updateMutation],
  );

  const deleteAccount = useCallback(async () => {
    const { data } = await deleteMutation();
    return Boolean(data?.deleteAccount);
  }, [deleteMutation]);

  return {
    settings: data?.me ?? null,
    fetching,
    updateProfile,
    saving,
    deleteAccount,
    deleting,
  };
}
